import { ApiResponseModel } from '../types/ApiResponseModel';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { SelectOption } from '../types/SelectOption';
import { SeniorSnapshotFileListItemModel } from '../types/SeniorSnapshotFileListItemModel';
import { SeniorSnapshotFormViewModel } from '../types/SeniorSnapshotFormViewModel';
import { SeniorSnapshotListItemModel } from '../types/SeniorSnapshotListItemModel';
import { SeniorSnapshotModel } from '../types/SeniorSnapshotModel';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

const buildLIstItemUrl = (
  schoolYear: string,
  lea?: string,
  school?: string,
  reviewStatus?: string
): string => {
  let baseUrl = `seniorSnapshot?calendarYear=${schoolYear}`;

  if (lea) {
    baseUrl += `&leaId=${lea}`;
  }
  if (school) {
    baseUrl += `&schoolId=${school}`;
  }
  if (reviewStatus) {
    baseUrl += `&status=${reviewStatus}`;
  }

  return baseUrl;
};

export const seniorSnapshotApiPaths = {
  create: 'seniorSnapshot',
  update: 'seniorSnapshot',
  approve: 'seniorSnapshot/approve',
  getListItems: (
    schoolYear: string,
    lea?: string,
    school?: string,
    reviewStatus?: string
  ) => buildLIstItemUrl(schoolYear, lea, school, reviewStatus),
  getById: (seniorSnapshotId: string): string =>
    `seniorSnapshot/${seniorSnapshotId}`,
  getForm: 'seniorSnapshot/form',
  getSchoolYears:
    'common/schoolyearsbymodule/formview/SeniorSnapshots?showLongYears=true',
  getSchoolsByLeaId: (leaId: string): string =>
    `/common/schoollistforlea/formview/${leaId}`,
  getLeas: '/common/lealistforcurrentuser/formview',
  uploadFile: (schoolYear: string, studentIdentifier: string,
    leaIdentifier: string): string =>
    `file/seniorsnapshot?schoolYear=${encodeURIComponent(
      schoolYear
    )}&studentIdentifier=${encodeURIComponent(studentIdentifier)}
    &leaIdentifier=${encodeURIComponent(leaIdentifier)}`,
  deleteFile: (leaId: string, id: string): string =>
    `file/seniorsnapshot/${leaId}/${id}`,
  getAllFiles: (leaId: string): string => `file/seniorsnapshot/${leaId}`,
  downloadFile: (leaId: string, metaId: string): string =>
    `file/seniorsnapshot/download/${leaId}/${metaId}`,
  getFilesByStudent: (
    schoolYear: string,
    leaId: string,
    msisId: string
  ): string => `file/seniorsnapshot/${schoolYear}/${leaId}/${msisId}`,
  getDataCollectionChecks: (schoolYear: string): string =>
    `seniorSnapshot/datacollectionchecks/${schoolYear}`,
};

export const seniorSnapshotServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const createSeniorSnapshot = async (
  body: SeniorSnapshotModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    if (!body.reviewStatus || body.reviewStatus === '') {
      body.reviewStatus = 'Pending Approval';
    }

    return await seniorSnapshotServiceClient(config).post(
      seniorSnapshotApiPaths.create,
      body
    );
  });
};

export const updateSeniorSnapshot = async (
  body: SeniorSnapshotModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest<unknown>(async () => {
    if (!body.reviewStatus || body.reviewStatus === '') {
      body.reviewStatus = 'Pending Approval';
    }

    return await seniorSnapshotServiceClient(config).put(
      seniorSnapshotApiPaths.update,
      body
    );
  });
};

export const getSeniorSnapshotListItems = async (
  schoolYear: string,
  lea?: string,
  school?: string,
  reviewStatus?: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SeniorSnapshotListItemModel[]>> => {
  return executeApiRequest<SeniorSnapshotListItemModel[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getListItems(schoolYear, lea, school, reviewStatus)
    );
  });
};

export const getSeniorSnapshotById = async (
  seniorSnapshotId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SeniorSnapshotModel>> => {
  return executeApiRequest<SeniorSnapshotModel>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getById(seniorSnapshotId)
    );
  });
};

export const getSeniorSnapshotFormViewModel = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SeniorSnapshotFormViewModel>> => {
  return executeApiRequest<SeniorSnapshotFormViewModel>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getForm
    );
  });
};

export const getSeniorSnapshotSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getSchoolYears
    );
  });
};

export const getSeniorSnapshotLeas = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getLeas
    );
  });
};

export const getSeniorSnapshotSchoolsByLea = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getSchoolsByLeaId(leaId)
    );
  });
};

export const approveSeniorSnapshots = async (
  ids: string[],
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest<unknown>(async () => {
    return await seniorSnapshotServiceClient(config).put(
      seniorSnapshotApiPaths.approve,
      { ids }
    );
  });
};


export const uploadSeniorSnapshotFile = async (
  schoolYear: string,
  studentIdentifier: string,
  leaIdentifier: string,
  file: File,
  config?: DataRequestConfig & {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }
): Promise<ApiResponseModel<{ id: string }>> => {
  const formData = new FormData();
  formData.append('file', file);

  return executeApiRequest<{ id: string }>(async () => {
    return await seniorSnapshotServiceClient(config).upload(
      seniorSnapshotApiPaths.uploadFile(schoolYear, studentIdentifier, leaIdentifier),
      formData,
      config?.onUploadProgress
    );
  });
};

export const deleteSeniorSnapshotFile = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await seniorSnapshotServiceClient(config).delete(
      seniorSnapshotApiPaths.deleteFile(leaId, id)
    );
  });
};

export const getAllSeniorSnapshotFiles = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SeniorSnapshotFileListItemModel[]>> => {
  return executeApiRequest<SeniorSnapshotFileListItemModel[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getAllFiles(leaId)
    );
  });
};

export const downloadSeniorSnapshotFile = async (
  leaId: string,
  metaId: string,
  config?: DataRequestConfig
): Promise<Blob> => {
  const response = await seniorSnapshotServiceClient(config).getFile<Blob>(
    seniorSnapshotApiPaths.downloadFile(leaId, metaId)
  );
  return response.data;
};

export const getSeniorSnapshotFilesByStudent = async (
  schoolYear: string,
  leaId: string,
  msisId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<SeniorSnapshotFileListItemModel[]>> => {
  return executeApiRequest<SeniorSnapshotFileListItemModel[]>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getFilesByStudent(schoolYear, leaId, msisId)
    );
  });
};

export const getDataCollectionChecks = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<boolean>> => {
  return executeApiRequest<boolean>(async () => {
    return await seniorSnapshotServiceClient(config).get(
      seniorSnapshotApiPaths.getDataCollectionChecks(schoolYear)
    );
  });
};